import { useCallback } from 'react'

import { Tooltip, ActionIcon, Switch, Checkbox } from '@mantine/core'

import { Link, routes } from '@redwoodjs/router'

import DnTable from 'src/components/Core/DnTable'
import EmployeeAvatarGroupForJobListing from 'src/components/Core/EmployeeAvatarGroupForJobListing'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { JobStatus } from 'src/graphql/types/jobListings'
import IconCopy from 'src/icons/IconCopy'
import IconEmptyInbox from 'src/icons/IconEmptyInbox'
import IconUsers from 'src/icons/IconUsers'
import { formatAddress } from 'src/lib/address.utils'
import { formatDate } from 'src/lib/formatters'

export interface SelectedJobListing {
  jobListingId: string
  title: string
}

interface JobsListingTableProps {
  jobsData: any[]
  loading: boolean
  setSelectedJobListing: (jobListingId: string | null) => void
  activeRecruiters: any[]
  onStatusChange: (jobListing: any, isActive: boolean) => void
  selectedJobListings?: SelectedJobListing[]
  setSelectedJobListings?: React.Dispatch<React.SetStateAction<SelectedJobListing[]>>
}

const JobsListingTable = ({
  jobsData,
  loading,
  setSelectedJobListing,
  activeRecruiters,
  onStatusChange,
  selectedJobListings = [],
  setSelectedJobListings,
}: JobsListingTableProps) => {

  const handleApplicantsNavigation = (event) => {
    event.stopPropagation()
  }

  const handleRowClick = (_rowIndex, rowData) => {
    setSelectedJobListing(rowData?.jobListingId ?? null)
  }

  const formatJobType = (type?: string | null) => {
    if (!type) {
      return '-'
    }

    return type.charAt(0) + type.slice(1).toLowerCase()
  }

  const handleSelectAll = useCallback(() => {
    if (!setSelectedJobListings) return

    if (
      jobsData.every((item) =>
        selectedJobListings.find(
          (selected) => selected.jobListingId === item.jobListingId
        )
      )
    ) {
      // Deselect all items on current page
      setSelectedJobListings((prev) =>
        prev.filter(
          (item) =>
            !jobsData.find(
              (jobListing) => jobListing.jobListingId === item.jobListingId
            )
        )
      )
    } else {
      // Select all items on current page that aren't already selected
      const duplicateFreeArray = jobsData.filter((item) => {
        return !selectedJobListings.find(
          (selected) => selected.jobListingId === item.jobListingId
        )
      })
      const temp = duplicateFreeArray.map((item) => ({
        jobListingId: item.jobListingId,
        title: item.title,
      }))
      setSelectedJobListings((prev) => [...prev, ...temp])
    }
  }, [jobsData, selectedJobListings, setSelectedJobListings])

  const handleSelectJobListing = useCallback(
    (jobListing) => {
      if (!setSelectedJobListings) return

      setSelectedJobListings((prev) =>
        prev.find((item) => item.jobListingId === jobListing.jobListingId)
          ? prev.filter((item) => item.jobListingId !== jobListing.jobListingId)
          : [
              ...prev,
              {
                jobListingId: jobListing.jobListingId,
                title: jobListing.title,
              },
            ]
      )
    },
    [setSelectedJobListings]
  )

  const columns = [
    // Only show select column if setSelectedJobListings is provided
    ...(setSelectedJobListings
      ? [
          {
            key: 'select',
            label: (
              <Checkbox
                size="xs"
                checked={
                  !loading && jobsData.length > 0
                    ? jobsData.every((item) =>
                        selectedJobListings.find(
                          (selected) =>
                            selected.jobListingId === item.jobListingId
                        )
                      )
                    : false
                }
                onChange={handleSelectAll}
              />
            ),
            render: (jobListing) => (
              <div>
                <Checkbox
                  size="xs"
                  checked={
                    !!selectedJobListings.find(
                      (item) => item.jobListingId === jobListing.jobListingId
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  onChange={() => handleSelectJobListing(jobListing)}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      key: 'jobListingId',
      label: 'ID',
      render: (jobListing) => (
        <Tooltip label={jobListing.jobListingId} withArrow>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(jobListing.jobListingId)
              toast('Copied to clipboard', 'success')
            }}
          >
            <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-600" />
          </ActionIcon>
        </Tooltip>
      ),
    },
    {
      key: 'title',
      label: 'Job Title',
      render: (jobListing) => jobListing.title ?? '-',
    },
    {
      key: 'type',
      label: 'Type',
      render: (jobListing) => formatJobType(jobListing.type),
    },
    {
      key: 'employees',
      label: 'Employees',
      render: (jobListing) => (
        <EmployeeAvatarGroupForJobListing
          leadEmployees={jobListing.employees || []}
          memberEmployees={jobListing.memberEmployees || []}
        />
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (jobListing) => formatAddress(jobListing.address) ?? '-',
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      render: (jobListing) =>
        jobListing.createdAt ? formatDate(jobListing.createdAt) : '-',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (jobListing) => (
        <div className="flex items-center justify-end gap-2">
          <Tooltip
            label={
              jobListing.status === JobStatus.ACTIVE
                ? 'Toggle to set this job listing to Draft. Draft job listings will not be visible to applicants.'
                : 'Toggle to set this job listing to Active. Active job listings will be visible to applicants.'
            }
            withArrow
            className="text-xs"
            multiline
            w={220}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <Switch
                key={`${jobListing.jobListingId}-${jobListing.status}`}
                size="xs"
                color="#1C93D7"
                checked={jobListing.status === JobStatus.ACTIVE}
                onChange={(event) => {
                  const isActive = event.currentTarget.checked
                  onStatusChange(jobListing, isActive)
                }}
              />
            </div>
          </Tooltip>
          <Tooltip label="Go to applicants" withArrow className="text-xs">
            <Link
              to={routes.applicants({
                jobListingId: jobListing.jobListingId,
                jobListingTitle: jobListing.title,
              })}
              onClick={handleApplicantsNavigation}
              className="no-trigger"
            >
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconUsers className="h-4 fill-doubleNickel-white stroke-doubleNickel-gray-600" />
              </ActionIcon>
            </Link>
          </Tooltip>
        </div>
      ),
    },
  ]

  return (
    <>
      <DnTable
        columns={columns}
        data={jobsData || []}
        loading={loading}
        onRowClick={handleRowClick}
        enablePagination={false}
        emptyStateMessage={
          <div className="flex flex-1 flex-col items-center justify-center">
            <IconEmptyInbox className="h-40 fill-none" />
            <div className="text-md font-medium text-doubleNickel-gray-800">
              No Records Found
            </div>
            <div className="text-sm text-doubleNickel-gray-600">
              Create a new job listing to get started
            </div>
          </div>
        }
      />
    </>
  )
}

export default JobsListingTable
